import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  SegmentedControl,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import i18next from 'i18next';
import { DataTable } from 'mantine-datatable';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IssueResponse } from '@/api';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { usePagination } from '@/hooks/usePagination';
import { EmptyState } from '../../../components/Common/EmptyState';
import { PageHeader } from '../../../components/Common/PageHeader';
import { absoluteTime, relativeTime } from '../../../lib/relative-time';
import { useRustyState } from '../../../state';

export function AccountIssuesStartPage() {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const { createLanguageURL } = useLanguageAwareRouting();
  const [dismissedFilter, setDismissedFilter] = useState<string>('active');
  const dismissedFilterRef = useRef(dismissedFilter);
  const [selected, setSelected] = useState<IssueResponse[]>([]);
  const [isBusy, setIsBusy] = useState(false);

  const fetchIssues = useCallback(
    async (params: { nextPage: number; [key: string]: any }) => {
      const { nextPage } = params;
      const filter = dismissedFilterRef.current;
      const dismissed = filter === 'active' ? false : filter === 'dismissed' ? true : undefined;
      const response = await api.getIssues({
        offset: nextPage === 1 ? 0 : 10 * (nextPage - 1),
        limit: 10,
        dismissed,
      });
      return {
        data: response.data || [],
        total: response.total || 0,
      };
    },
    [api]
  );

  const pagination = usePagination<IssueResponse>({
    perPage: 10,
    fetchData: fetchIssues,
  });

  const handleFilterChange = (value: string) => {
    setDismissedFilter(value);
    dismissedFilterRef.current = value;
    setSelected([]);
    pagination.loadPage(1);
  };

  const handleDismiss = async (id: string) => {
    try {
      await api.dismissIssue(id);
      setSelected((prev) => prev.filter((i) => i.id !== id));
      pagination.loadPage(pagination.page);
    } catch (err) {
      console.error('Failed to dismiss issue:', err);
    }
  };

  const handleBulkDismiss = async () => {
    const ids = selected.filter((i) => !i.dismissed).map((i) => i.id);
    if (ids.length === 0) {
      return;
    }
    setIsBusy(true);
    try {
      const result = await api.bulkDismissIssues(ids);
      notifications.show({
        title: t('issues.bulkDismissedTitle'),
        message: t('issues.bulkDismissed', { count: result.dismissed }),
        color: 'green',
      });
      setSelected([]);
      pagination.loadPage(pagination.page);
    } catch (err) {
      console.error('Failed to dismiss issues:', err);
    } finally {
      setIsBusy(false);
    }
  };

  const dateTime = (value: string) => {
    const locale = (i18next.language || 'en').split('-')[0];
    return (
      <Tooltip inline label={absoluteTime(value, locale)}>
        <Text size="sm">{relativeTime(value, locale)}</Text>
      </Tooltip>
    );
  };

  const getIssueTypeLabel = (issueType: string) => {
    const key = `issues.types.${issueType}`;
    const translated = t(key);
    return translated !== key ? translated : issueType;
  };

  const getIssueCodeLabel = (issueCode: string) => {
    const key = `issues.codes.${issueCode}`;
    const translated = t(key);
    return translated !== key ? translated : issueCode;
  };

  const columns = [
    {
      accessor: 'created_at',
      title: t('issues.createdAt'),
      width: 130,
      render: (row: IssueResponse) => dateTime(row.created_at),
    },
    {
      accessor: 'form_title',
      title: t('issues.formName'),
      render: (row: IssueResponse) =>
        row.form_id ? (
          <Anchor href={`/#${createLanguageURL(`/account/forms/${row.form_id}`)}`} size="sm">
            {row.form_title || row.form_id.slice(0, 8)}
          </Anchor>
        ) : (
          <Text size="sm" c="dimmed">
            -
          </Text>
        ),
    },
    {
      accessor: 'issue_type',
      title: t('issues.issueType'),
      render: (row: IssueResponse) => (
        <Badge variant="light">{getIssueTypeLabel(row.issue_type)}</Badge>
      ),
    },
    {
      accessor: 'issue_code',
      title: t('issues.issueCode'),
      render: (row: IssueResponse) => (
        <Badge variant="light" color="gray">
          {getIssueCodeLabel(row.issue_code)}
        </Badge>
      ),
    },
    {
      accessor: 'details',
      title: t('issues.details'),
      render: (row: IssueResponse) => (
        <Text size="sm" lineClamp={2}>
          {row.details || '-'}
        </Text>
      ),
    },
    {
      accessor: 'message_id',
      title: t('issues.message'),
      render: (row: IssueResponse) =>
        row.message_id ? (
          <Anchor href={`/#${createLanguageURL(`/account/message/${row.message_id}`)}`} size="sm">
            {t('issues.viewMessage')}
          </Anchor>
        ) : (
          <Text size="sm" c="dimmed">
            -
          </Text>
        ),
    },
    {
      accessor: 'actions',
      title: '',
      render: (row: IssueResponse) =>
        !row.dismissed ? (
          <Tooltip label={t('issues.dismiss')}>
            <ActionIcon variant="subtle" color="green" onClick={() => handleDismiss(row.id)}>
              <IconCheck size={16} />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Badge color="gray" variant="light">
            {t('issues.dismissed')}
          </Badge>
        ),
    },
  ];

  const selectable = selected.filter((i) => !i.dismissed);

  return (
    <>
      <PageHeader title={t('issues.title')} subtitle={t('issues.description')} />
      <Stack gap="md">
        <Group justify="space-between">
          <SegmentedControl
            value={dismissedFilter}
            onChange={handleFilterChange}
            data={[
              { label: t('issues.active'), value: 'active' },
              { label: t('issues.dismissed'), value: 'dismissed' },
              { label: t('issues.all'), value: 'all' },
            ]}
          />
          {selectable.length > 0 && (
            <Group gap="xs">
              <Text size="sm" c="dimmed">
                {t('issues.selected', { count: selectable.length })}
              </Text>
              <Button
                size="compact-sm"
                color="green"
                loading={isBusy}
                leftSection={<IconCheck size={16} />}
                onClick={handleBulkDismiss}
              >
                {t('issues.dismissSelected')}
              </Button>
              <Button
                size="compact-sm"
                variant="subtle"
                color="gray"
                onClick={() => setSelected([])}
              >
                {t('issues.clearSelection')}
              </Button>
            </Group>
          )}
        </Group>
        {pagination.error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            {pagination.error}
          </Alert>
        )}
        <DataTable
          minHeight={(pagination.data?.length ?? 0) === 0 ? 280 : undefined}
          emptyState={
            <EmptyState
              icon={IconAlertTriangle}
              title={t('issues.noIssuesFound')}
              description={t(
                'issues.emptyStateDescription',
                'Delivery problems with auto-responses, notifications, and integrations show up here.'
              )}
            />
          }
          withTableBorder
          borderRadius="md"
          shadow="xs"
          records={pagination.data || []}
          columns={columns}
          selectedRecords={selected}
          onSelectedRecordsChange={setSelected}
          isRecordSelectable={(row: IssueResponse) => !row.dismissed}
          totalRecords={pagination.total}
          recordsPerPage={pagination.perPage}
          page={pagination.page}
          onPageChange={pagination.setPage}
          fetching={pagination.loading || isBusy}
          noRecordsText={t('issues.noIssuesFound')}
        />
      </Stack>
    </>
  );
}
